/**
 * useFolderSyncs Hook
 * API hooks for folder synchronization operations
 *
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import type { FolderSync, FolderSyncCreate, FolderSyncUpdate } from '../../api/ApiClient';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook } from './factories';

// Use types from generated API client - re-export for convenience
export type { FolderSync, FolderSyncCreate, FolderSyncUpdate } from '../../api/ApiClient';

// Query Keys
export const folderSyncKeys = {
  all: ['folderSyncs'] as const,
  lists: () => [...folderSyncKeys.all, 'list'] as const,
  list: (filters: string) => [...folderSyncKeys.lists(), { filters }] as const,
  details: () => [...folderSyncKeys.all, 'detail'] as const,
  detail: (id: string) => [...folderSyncKeys.details(), id] as const,
};

// --- Query Hooks ---

/**
 * Get list of all folder syncs
 */
export const useFolderSyncs = createQueryHook<FolderSync[], void>({
  queryKey: () => folderSyncKeys.lists(),
  queryFn: (apiClient) =>
    apiClient.listFolderSyncsApiFolderSyncsGet().then(response => response.data ?? []),
  logContext: 'GET /api/folder-syncs/',
  staleTime: QUERY_STALE_TIME.MEDIUM,
  onSuccess: (data) => {
    log.api.response('GET', '/api/folder-syncs/', 200, { count: data?.length ?? 0 });
  },
});

/**
 * Get single folder sync by ID
 */
export const useFolderSync = createQueryHook<FolderSync, string>({
  queryKey: (id) => folderSyncKeys.detail(id),
  queryFn: (apiClient, id) =>
    apiClient.getFolderSyncApiFolderSyncsSyncIdGet(id).then(response => response.data),
  logContext: 'GET /api/folder-syncs/:id',
  staleTime: QUERY_STALE_TIME.MEDIUM,
});

// --- Mutation Hooks ---

/**
 * Create a new folder sync
 */
export const useCreateFolderSync = createMutationHook<FolderSync, FolderSyncCreate>({
  mutationFn: (apiClient, data) =>
    apiClient.createFolderSyncApiFolderSyncsPost(data).then(response => response.data),
  invalidateQueries: [folderSyncKeys.lists()],
  logContext: 'POST /api/folder-syncs/',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('folder_sync_created', 'FolderSyncs');
    log.api.response('POST', '/api/folder-syncs/', 200, { id: data.id });
  },
});

/**
 * Update an existing folder sync
 */
export const useUpdateFolderSync = createMutationHook<
  FolderSync,
  { id: string; data: FolderSyncUpdate }
>({
  mutationFn: (apiClient, { id, data }) =>
    apiClient.updateFolderSyncApiFolderSyncsSyncIdPut(id, data).then(response => response.data),
  invalidateQueries: [folderSyncKeys.lists()],
  logContext: 'PUT /api/folder-syncs/:id',
  logMethod: 'PUT',
  onSuccess: (data) => {
    log.user.action('folder_sync_updated', 'FolderSyncs');
    log.api.response('PUT', `/api/folder-syncs/${data.id}`, 200, { id: data.id });
  },
});

/**
 * Delete a folder sync
 */
export const useDeleteFolderSync = createMutationHook<void, string>({
  mutationFn: (apiClient, id) =>
    apiClient.deleteFolderSyncApiFolderSyncsSyncIdDelete(id).then(() => undefined),
  invalidateQueries: [folderSyncKeys.lists()],
  logContext: 'DELETE /api/folder-syncs/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, id) => {
    log.user.action('folder_sync_deleted', 'FolderSyncs');
    log.api.response('DELETE', `/api/folder-syncs/${id}`, 200, { id });
  },
});

/**
 * Trigger sync for a folder sync
 */
export const useSyncFolderSync = createMutationHook<
  { success: boolean; message: string; sync_id: string },
  string
>({
  mutationFn: (apiClient, id) =>
    apiClient.triggerFolderSyncApiFolderSyncsSyncIdSyncPost(id).then(response => response.data),
  invalidateQueries: [folderSyncKeys.lists()],
  logContext: 'POST /api/folder-syncs/:id/sync',
  logMethod: 'POST',
  onSuccess: (_data, id) => {
    log.user.action('folder_sync_triggered', 'FolderSyncs');
    log.api.response('POST', `/api/folder-syncs/${id}/sync`, 200, { id });
  },
});

/**
 * Toggle folder sync status (active/paused)
 */
export const useToggleFolderSyncStatus = createMutationHook<
  FolderSync,
  { id: string; status: 'active' | 'paused' }
>({
  mutationFn: (apiClient, { id, status }) =>
    apiClient.updateFolderSyncApiFolderSyncsSyncIdPut(id, { status }).then(response => response.data),
  invalidateQueries: [folderSyncKeys.lists()],
  logContext: 'PUT /api/folder-syncs/:id',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('folder_sync_status_changed', 'FolderSyncs');
    log.api.response('PUT', `/api/folder-syncs/${data.id}`, 200, { id: data.id, status: variables.status });
  },
});
